/**
 * QueryHistoryItem Component
 *
 * Displays a single query history entry with SQL preview,
 * execution stats, and action buttons.
 */

import { useState, useCallback } from 'react';
import { Check, X, Play, Copy, ChevronRight } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { QueryHistoryEntry } from '../../../query-history/types/queryHistoryTypes';
import { getQueryHistoryPreview } from '../../../query-history/utils/queryHistoryPreview';
import {
  formatRelativeTime,
  formatExecutionTime,
} from '../utils/timestampFormatting';
import { formatRowCount } from '../../common/utils/formatRowCount';

type QueryHistoryItemProps = {
  /** The query history entry to display */
  entry: QueryHistoryEntry;
  /** Callback when "Run Again" is clicked */
  onRunAgain: (entry: QueryHistoryEntry) => void;
  /** Whether this item is selected (for keyboard navigation) */
  isSelected?: boolean;
};

export function QueryHistoryItem({
  entry,
  onRunAgain,
  isSelected,
}: QueryHistoryItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRunAgain = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRunAgain(entry);
    },
    [entry, onRunAgain]
  );

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      await writeClipboardText(entry.sql);
    },
    [entry.sql]
  );

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const preview = getQueryHistoryPreview(entry.sql, 80);

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={`
        group border-b border-border/30 last:border-b-0
        ${isSelected ? 'bg-accent' : 'hover:bg-muted/50'}
        transition-colors
      `}
      data-testid="query-history-item"
    >
      {/* Main row */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
        onClick={handleToggleExpand}
      >
        {/* Expand/collapse toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleExpand();
          }}
          aria-label={isExpanded ? 'Collapse query' : 'Expand query'}
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform duration-200', isExpanded && 'rotate-90')}
          />
        </Button>

        {/* Success/failure indicator */}
        <div
          className={`
            shrink-0 flex items-center justify-center w-5 h-5 rounded-full
            ${entry.success ? 'text-green-500' : 'text-destructive'}
          `}
          title={entry.success ? 'Query succeeded' : 'Query failed'}
        >
          {entry.success ? (
            <Check className="h-3.5 w-3.5" aria-label="Success" />
          ) : (
            <X className="h-3.5 w-3.5" aria-label="Failed" />
          )}
        </div>

        {/* SQL preview */}
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-mono truncate text-foreground"
            title={entry.sql}
          >
            {preview}
          </p>
        </div>

        {/* Stats */}
        <div className="shrink-0 flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
          {entry.success && entry.executionTimeMs !== undefined && (
            <span title="Execution time">
              {formatExecutionTime(entry.executionTimeMs)}
            </span>
          )}
          {entry.success && entry.rowCount !== undefined && (
            <span title="Row count">{formatRowCount(entry.rowCount)}</span>
          )}
          <span title={entry.executedAt.toLocaleString()}>
            {formatRelativeTime(entry.executedAt)}
          </span>
        </div>

        {/* Actions (visible on hover) */}
        <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRunAgain}
            title="Run query again"
          >
            <Play className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleCopy}
            title="Copy SQL to clipboard"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Expanded SQL view */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 pl-12 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="rounded border bg-muted/30 p-2">
            <pre className="text-xs font-mono whitespace-pre-wrap text-foreground/80 max-h-40 overflow-y-auto">
              {entry.sql}
            </pre>
          </div>
          {!entry.success && entry.errorMessage && (
            <div className="mt-2 rounded border border-destructive/30 bg-destructive/10 p-2">
              <p className="text-xs text-destructive">{entry.errorMessage}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
